import { PatientDetailTabKey } from "@/domains/shared/models/IParentDetail";
import { SecretaryPermissions } from "@/domains/shared/models/ISecretaryPermissions";


export const PATIENT_DETAIL_TAB_PERM_KEYS: Record<string, PatientDetailTabKey> = {
    generalView: "generalView",
    patientDetails: "patientDetails",
    anamnesis: "anamnesis",
    goals: "goals",
    evolution: "evolution",
    orientations: "orientations",
    observations: "observations",
    meetingMinutes: "meetingMinutes",
    files: "files",
    reports: "reports",
    finance: "finance",
    billing: "billing",
};

function readFlag(obj: unknown, ...path: string[]): boolean {
    let current: unknown = obj;
    for (const key of path) {
        if (typeof current !== "object" || current == null || !(key in current)) {
            return false;
        }
        current = (current as Record<string, unknown>)[key];
    }
    return current === true;
}

export function hasSecretaryPermission(
    permissions: SecretaryPermissions | null | undefined,
    ...path: string[]
): boolean {
    if (!permissions) {
        return false;
    }
    return readFlag(permissions, ...path);
}

export function secretaryHasAgendaView(permissions: SecretaryPermissions | null | undefined): boolean {
    return hasSecretaryPermission(permissions, "agenda", "view");
}

export function secretaryHasAgendaEdit(permissions: SecretaryPermissions | null | undefined): boolean {
    return hasSecretaryPermission(permissions, "agenda", "edit");
}

export function secretaryHasRequestsView(permissions: SecretaryPermissions | null | undefined): boolean {
    return hasSecretaryPermission(permissions, "requests", "view");
}

export function secretaryHasRequestsEdit(permissions: SecretaryPermissions | null | undefined): boolean {
    return hasSecretaryPermission(permissions, "requests", "edit");
}

export function secretaryHasChatView(permissions: SecretaryPermissions | null | undefined): boolean {
    return hasSecretaryPermission(permissions, "chat", "view");
}

export function secretaryHasChatEdit(permissions: SecretaryPermissions | null | undefined): boolean {
    return hasSecretaryPermission(permissions, "chat", "edit");
}

export function secretaryHasTeamChatView(permissions: SecretaryPermissions | null | undefined): boolean {
    return hasSecretaryPermission(permissions, "teamChat", "view");
}

export function secretaryHasTeamChatEdit(permissions: SecretaryPermissions | null | undefined): boolean {
    return hasSecretaryPermission(permissions, "teamChat", "edit");
}

export function secretaryHasChatSettingsEdit(permissions: SecretaryPermissions | null | undefined): boolean {
    return hasSecretaryPermission(permissions, "chatSettings", "edit");
}

export function secretaryHasChatSettingsView(permissions: SecretaryPermissions | null | undefined): boolean {
    return hasSecretaryPermission(permissions, "chatSettings", "view");
}

export function secretaryHasFinanceMenu(permissions: SecretaryPermissions | null | undefined): boolean {
    return (
        hasSecretaryPermission(permissions, "menus", "finance") &&
        hasSecretaryPermission(permissions, "delegatedModules", "finance")
    );
}

export function secretaryHasAiReportsAccess(permissions: SecretaryPermissions | null | undefined): boolean {
    return (
        hasSecretaryPermission(permissions, "delegatedModules", "aiReports") &&
        secretaryHasPatientTabView(permissions, "reports")
    );
}

export function secretaryHasPatientTabView(
    permissions: SecretaryPermissions | null | undefined,
    tabKey: PatientDetailTabKey
): boolean {
    return hasSecretaryPermission(permissions, "patientTabs", tabKey, "view");
}

export function secretaryHasPatientTabEdit(
    permissions: SecretaryPermissions | null | undefined,
    tabKey: PatientDetailTabKey
): boolean {
    return hasSecretaryPermission(permissions, "patientTabs", tabKey, "edit");
}

export function secretaryCanViewPatientDetailTab(
    permissions: SecretaryPermissions | null | undefined,
    tabValue: string,
    options?: { filesTabEnabled?: boolean; aiReportsDelegated?: boolean; financeDelegated?: boolean }
): boolean {
    const permKey = PATIENT_DETAIL_TAB_PERM_KEYS[tabValue];
    if (!permKey) {
        return false;
    }

    if (permKey === "files" && options?.filesTabEnabled === false) {
        return false;
    }

    if (permKey === "reports" && options?.aiReportsDelegated === false) {
        return false;
    }

    if ((permKey === "finance" || permKey === "billing") && !options?.financeDelegated) {
        return false;
    }

    return secretaryHasPatientTabView(permissions, permKey);
}

export function coerceSecretaryPermissions(payload: SecretaryPermissions): SecretaryPermissions {
    const next = structuredClone(payload) as SecretaryPermissions;

    for (const section of ["agenda", "requests", "chat", "teamChat", "chatSettings"] as const) {
        const block = next[section];
        if (typeof block === "object" && block != null) {
            (block as Record<string, boolean>).view = true;
        }
    }

    const patientTabs = next.patientTabs;
    if (typeof patientTabs === "object" && patientTabs != null) {
        for (const tab of Object.values(patientTabs)) {
            if (typeof tab === "object" && tab != null && (tab as Record<string, boolean>).edit === true) {
                (tab as Record<string, boolean>).view = true;
            }
        }
    }

    if (
        hasSecretaryPermission(next, "delegatedModules", "finance") &&
        typeof next.menus === "object" &&
        next.menus != null
    ) {
        (next.menus as Record<string, boolean>).finance = true;
    }

    return next;
}
